import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import React, { useState, useEffect } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { db } from "../../firebase";
import DeleteButtonFile from "../Buttons/DeleteButtonFile";
import { showCustomToast } from "../Misc/CustomToast";
import pdfIcon from "../../images/pdf-icon-small.png";
import wordIcon from "../../images/word-icon-small.png";
import excelIcon from "../../images/excel-icon-small.png";
import zIcon from "../../images/icons/7z-small-icon.png";
import csvIcon from "../../images/icons/csv-small-icon.png";
import gifIcon from "../../images/icons/gif-small-icon.png";
import jpgIcon from "../../images/icons/jpg-small-icon.png";
import pngIcon from "../../images/icons/png-small-icon.png";
import pptIcon from "../../images/icons/ppt-small-icon.png";
import rarIcon from "../../images/icons/rar-small-icon.png";
import txtIcon from "../../images/icons/txt-small-icon.png";
import zipIcon from "../../images/icons/zip-small-icon.png";
import "./UploaderAraEncapsulated.css";

function UploaderGeneralInfosEncapsulatedBis(props) {
  const maxSizePicFile = 100 * 1024 * 1024; // 5 Mo
  const [, setFiles] = useState([]);
  const [fileLinks, setFileLinks] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [, setIsLoading] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);

  const getFileIcon = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return pdfIcon;
      case "doc":
        return wordIcon;
      case "docx":
        return wordIcon;
      case "xls":
        return excelIcon;
      case "xlsx":
        return excelIcon;
      case "ppt":
        return pptIcon;
      case "pptx":
        return pptIcon;
      case "txt":
        return txtIcon;
      case "csv":
        return csvIcon;
      case "zip":
        return zipIcon;
      case "rar":
        return rarIcon;
      case "7z":
        return zIcon;
      case "jpg":
        return jpgIcon;
      case "jpeg":
        return jpgIcon;
      case "png":
        return pngIcon;
      case "gif":
        return gifIcon;
      default:
        return "📄";
    }
  };

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length <= 3) {
      const selectedFiles = event.target.files;
      setFiles(selectedFiles);
      await uploadFilesAndGetUrls(selectedFiles, props.clientId);
    } else {
      showCustomToast("You can only upload up to 3 files.", "error");
    }
  };

  const fetchFileUrls = async () => {
    const docRef = doc(db, "clients", props.clientId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      setFileLinks(docSnap.data().fileUrls || []);
    } else {
      console.log("No such document!");
    }
  };
  useEffect(() => {
    fetchFileUrls();
  }, [props.clientId]);

  const uploadFilesAndGetUrls = async (fileList, docId) => {
    setIsLoading(true);
    const storage = getStorage();
    const promises = [];
    setShowProgressBar(true);
    setUploadingFiles([]);

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.size > maxSizePicFile) {
        showCustomToast(`File size exceeds the limit: ${file.name}`, "error");
        continue;
      }

      setUploadingFiles((prevState) => [
        ...prevState,
        { name: file.name, progress: 0 },
      ]);

      const filePath = `Documents/generalInfos/${docId}/${file.name}`;

      // const filePath = `PreAdmission/${props.clientId}/${file.name}`;
      const storageRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on("state_changed", (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

        if (progress === 100) {
          setShowProgressBar(false);
        }
        setUploadingFiles((prevState) =>
          prevState.map((f) => (f.name === file.name ? { ...f, progress } : f))
        );
      });

      promises.push(
        new Promise((resolve, reject) => {
          uploadTask
            .then((snapshot) => {
              return getDownloadURL(snapshot.ref);
            })
            .then((url) => {
              resolve({ name: file.name, url });
            })
            .catch((error) => {
              reject(error);
            });
        })
      );
    }

    try {
      const fileUrls = await Promise.all(promises);
      const clientRef = doc(db, "clients", docId);
      const docSnap = await getDoc(clientRef);
      document.getElementById("uploadFiles").value = "";
      const existingUrls =
        docSnap.exists() && docSnap.data().fileUrls
          ? docSnap.data().fileUrls
          : [];
      await updateDoc(clientRef, {
        fileUrls: [...existingUrls, ...fileUrls],
      });

      setIsLoading(false);
      setUploadingFiles((prevState) =>
        prevState.filter((f) => !fileUrls.includes(f.name))
      );
      fetchFileUrls();
      return fileUrls;
    } catch (error) {
      console.error("Erreur lors de l'upload des fichiers: ", error);
    }
  };

  const deleteFile = async (fileUrl, fileName, noteId) => {
    const storage = getStorage();
    const filePath = `Documents/generalInfos/${props.clientId}/${fileName}`;
    const storageRef = ref(storage, filePath);

    try {
      await deleteObject(storageRef);
      const newFileLinks = fileLinks.filter((file) => file.url !== fileUrl);
      setFileLinks(newFileLinks);
      const noteRef = doc(db, "clients", noteId);

      await updateDoc(noteRef, {
        fileUrls: newFileLinks,
      });
      document.getElementById("uploadFiles").value = "";

      showCustomToast("File successfully deleted!", "success");
    } catch (error) {
      console.log("Error deleting file:", error);
      showCustomToast("Error deleting file", "error");
    }
  };

  return (
    <>
      <div className="ara-uploader">
        <section className="ara-upload-card">
          <div className="ara-upload-heading">
            <div className="ara-upload-icon"><CloudUploadOutlinedIcon /></div>
            <div>
              <span className="ara-kicker">Secure document library</span>
              <h3>Upload passport pictures</h3>
              <p>Add up to 3 files. Uploads start automatically after selection.</p>
            </div>
          </div>
          <label className="ara-dropzone" htmlFor="uploadFiles">
            <CloudUploadOutlinedIcon className="ara-dropzone-icon" />
              <strong>Drop files here or browse</strong>
              <span>Images, PDF and office files · 100 MB maximum per file</span>
              <input
                type="file"
                id="uploadFiles"
                multiple
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z"
                style={{
                  width: "0.1px",
                  height: "0.1px",
                  opacity: 0,
                  overflow: "hidden",
                  position: "absolute",
                  zIndex: -1,
                }}
              />
              <em>Select files</em>
          </label>
        </section>

          <section className="ara-files-card">
            <div className="ara-section-heading">
              <div><span className="ara-kicker">Client library</span><h3>Uploaded documents</h3></div>
              <span className="ara-count">{fileLinks.length}</span>
            </div>
            <div className="ara-existing-list">
              {/* Affichage des fichiers déjà téléchargés */}
              {fileLinks && fileLinks.length > 0 ? (
                fileLinks.map((file, index) => (
                    <div key={index} className="ara-existing-file">
                    <div style={{ width: "10%", textAlign: "center" }}>
                      <img
                        src={getFileIcon(file.name)}
                        alt="File Icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      {/* <span role="img" aria-label="File Icon">
                        📄
                      </span> */}
                    </div>
                    <div style={{ width: "80%" }}>
                      <a
                        style={{
                          display: "inline-block",
                          marginLeft: "10px",
                          marginRight: "10px",
                          width: "90%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {file.name}
                      </a>
                    </div>
                    <div style={{ width: "10%", textAlign: "center" }}>
                      <DeleteButtonFile
                        fileName={file.name}
                        onClick={() =>
                          deleteFile(file.url, file.name, props.clientId)
                        }
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="ara-empty">No documents uploaded yet.</div>
              )}

              {/* Affichage des fichiers en cours de téléchargement */}

              {showProgressBar &&
                uploadingFiles.map((file, index) => (
                  <div
                    key={index}
                    className="ara-progress-item"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div
                      style={{
                        width: "100%",
                        paddingLeft: "5px",
                        paddingRight: "5px",
                      }}
                    >
                      <div style={{ flex: 2 }}>
                        Uploading...
                        {/* {file.name} */}
                      </div>
                      <div style={{ flex: 8 }}>
                        <div
                          style={{
                            height: "15px",
                            width: "100%",
                            borderRadius: "5px",
                            backgroundColor: "lightgray",
                          }}
                        >
                          <div
                            style={{
                              width: `${file.progress}%`,
                              height: "100%",
                              backgroundColor: "#007bff",
                              borderRadius: "5px",
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </section>
      </div>
    </>
  );
}

export default UploaderGeneralInfosEncapsulatedBis;
